import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import api from "../../lib/api";

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// status keys → styling
const LEGEND = [
  { key: "whole", label: "Whole house booked", cls: "bg-rose text-white" },
  { key: "full", label: "Fully booked (6 guests / all suites)", cls: "bg-red-700 text-white" },
  { key: "suite", label: "Suite(s) booked", cls: "bg-amber-400 text-forest" },
  { key: "blocked", label: "Blocked / OTA", cls: "bg-stone-400 text-white" },
  { key: "available", label: "Available", cls: "bg-sand text-stone-500" },
];

export default function MasterCalendar() {
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/rooms"),
      api.get("/admin/calendar"),
    ]).then(([r, c]) => {
      setRooms(r.data || []);
      setBookings(c.data?.bookings || []);
      setBlocked(c.data?.blocked || []);
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const wholeIds = useMemo(() => new Set(rooms.filter((r) => r.whole_house).map((r) => r.id)), [rooms]);
  const suiteCount = useMemo(() => rooms.filter((r) => !r.whole_house).length, [rooms]);
  const roomName = useCallback((id) => id === "all" ? "All suites" : (rooms.find((r) => r.id === id)?.name || "Suite"), [rooms]);

  const dayStatus = useCallback((D) => {
    const covers = (s, e) => s <= D && D < e;
    const wholeB = bookings.filter((b) => wholeIds.has(b.room_id) && covers(b.check_in, b.check_out));
    const suiteB = bookings.filter((b) => !wholeIds.has(b.room_id) && covers(b.check_in, b.check_out));
    const blks = blocked.filter((bl) => covers(bl.start, bl.end));
    const allBlock = blks.some((bl) => bl.room_id === "all");
    const guests = suiteB.reduce((n, b) => n + (parseInt(b.guests) || 1), 0);
    const bookedSuiteIds = new Set(suiteB.map((b) => b.room_id));
    const blockedSuiteIds = new Set(blks.filter((bl) => bl.room_id !== "all").map((bl) => bl.room_id));
    const usedSuites = new Set([...bookedSuiteIds, ...blockedSuiteIds]);

    const detail = [];
    wholeB.forEach((b) => detail.push(`Whole house — ${b.guest_name || b.code || "guest"}`));
    suiteB.forEach((b) => detail.push(`${roomName(b.room_id)} — ${b.guest_name || b.code || "guest"} (${b.guests || 1}g)`));
    blks.forEach((bl) => detail.push(`Blocked: ${roomName(bl.room_id)}${bl.reason ? ` · ${bl.reason}` : ""}`));

    let key = "available";
    if (wholeB.length) key = "whole";
    else if (allBlock) key = "full";
    else if (suiteB.length && (guests >= 6 || (suiteCount > 0 && usedSuites.size >= suiteCount))) key = "full";
    else if (suiteB.length) key = "suite";
    else if (blks.length) key = "blocked";

    return { key, detail, guests, suites: bookedSuiteIds.size };
  }, [bookings, blocked, wholeIds, suiteCount, roomName]);

  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const lead = first.getDay();
    const dim = new Date(view.y, view.m + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= dim; d++) out.push(d);
    return out;
  }, [view]);

  const clsFor = (key) => LEGEND.find((l) => l.key === key)?.cls || "bg-sand text-stone-500";
  const shift = (delta) => setView((v) => {
    const nm = v.m + delta;
    return { y: v.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
  });
  const todayStr = ymd(now.getFullYear(), now.getMonth(), now.getDate());

  return (
    <div className="card-lux p-6" data-testid="master-calendar">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl text-forest">Master calendar</h3>
        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="p-2 rounded-lg hover:bg-sand" aria-label="Previous month"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-forest w-40 text-center" data-testid="cal-month">{MONTHS[view.m]} {view.y}</span>
          <button onClick={() => shift(1)} data-testid="cal-next" className="p-2 rounded-lg hover:bg-sand" aria-label="Next month"><ChevronRight size={16} /></button>
          <button onClick={load} data-testid="cal-refresh" className="ml-1 p-2 rounded-lg hover:bg-sand text-rose" aria-label="Refresh"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4">
        {LEGEND.map((l) => (
          <span key={l.key} className="flex items-center gap-1.5 text-xs text-stone-600" data-testid={`legend-${l.key}`}>
            <span className={`h-3 w-3 rounded ${l.cls.split(" ")[0]}`} /> {l.label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {WD.map((w) => <div key={w} className="text-[11px] font-semibold uppercase tracking-wide text-stone-400 py-1">{w}</div>)}
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const D = ymd(view.y, view.m, d);
          const st = dayStatus(D);
          const isToday = D === todayStr;
          return (
            <div key={D} title={st.detail.join("\n") || "Available"} data-testid={`cal-day-${D}`}
              className={`min-h-[54px] rounded-lg p-1.5 text-left ${clsFor(st.key)} ${isToday ? "ring-2 ring-forest" : ""}`}>
              <div className="text-xs font-bold leading-none">{d}</div>
              {st.key === "whole" && <div className="text-[9px] mt-1 leading-tight font-semibold">Whole house</div>}
              {st.key === "full" && <div className="text-[9px] mt-1 leading-tight font-semibold">Full</div>}
              {st.key === "suite" && <div className="text-[9px] mt-1 leading-tight font-semibold">{st.suites} suite{st.suites > 1 ? "s" : ""}</div>}
              {st.key === "blocked" && <div className="text-[9px] mt-1 leading-tight font-semibold">Blocked</div>}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-stone-400 mt-3">Hover a day to see who is booked. "Whole house" = exclusive-use reservation; "Full" = all suites taken or the 6-guest house cap reached.</p>
    </div>
  );
}
